import { useState, useEffect } from "react";
import { Box, Flex, Heading, Text, Stack, HStack, Icon, Button, Input, Badge } from "@chakra-ui/react";
import { KeyRound, TimerReset } from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { showToast } from "@/components/AppToaster";
import { colors } from "@/theme/colors";
import { APP_VERSION, BUILD_ID, BUILD_DATE } from "@/version";
import { bundleHash, fetchSwVersion } from "@/lib/buildInfo";

const HAIRLINE = "#BFE3E8";

const SectionHeader = ({ children }) => (
  <Text fontFamily="heading" fontWeight="600" fontSize="sm" color={colors.tealHover}
    textTransform="uppercase" letterSpacing="0.08em" pb="2" mb="1" borderBottom="2px solid" borderColor={HAIRLINE}>
    {children}
  </Text>
);

const Row = ({ label, note, children, last = false, testId }) => (
  <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap="3"
    direction={{ base: "column", md: "row" }} py="3.5"
    borderBottom={last ? "none" : "1px solid"} borderColor={colors.border} data-testid={testId}>
    <Box>
      <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{label}</Text>
      {note && <Text fontSize="xs" color={colors.textSecondary} mt="0.5" maxW="420px">{note}</Text>}
    </Box>
    <Box>{children}</Box>
  </Flex>
);

// Session sign-out policy is ROLE-BASED (see ROLE_IDLE_MINUTES in lib/idle.js):
// Admins 30 min, Supervisors/Field 60 min of inactivity, installed app exempt.
const SessionSecurityRow = () => (
  <Box py="3.5" data-testid="session-security-row">
    <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap="3"
      direction={{ base: "column", md: "row" }}>
      <Box>
        <HStack gap="1.5">
          <Icon as={TimerReset} boxSize="4" color={colors.teal} />
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>Session security</Text>
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary} mt="0.5" maxW="420px">
          Browser sessions sign out automatically after inactivity, based on role:
          Admins after 30 minutes, Supervisors after 60 minutes — each with a 1-minute warning.
          The installed mobile app is exempt so field workers stay signed in mid-shift.
        </Text>
      </Box>
    </Flex>
  </Box>
);
export default function AccountSettings() {
  const { user, profile, role, companyName, isSuperAdmin, activeCompanyId, reload } = useAuth();
  const [name, setName] = useState(profile?.full_name || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [pwSending, setPwSending] = useState(false);
  const [pwSent, setPwSent] = useState(false);

  const displayName = (profile?.full_name || "").trim() || user?.email || "User";
  const initials = displayName.split(/\s+/).filter(Boolean).map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = isSuperAdmin ? "Hakina Super-Admin" : role ? ROLE_LABELS[role] : "Pending";
  const scope = isSuperAdmin ? "All companies (Hakina Safety)" : companyName || "—";
  const showSessionSecurity = role === "org_admin" || (isSuperAdmin && activeCompanyId);

  const saveProfile = async () => {
    const clean = name.trim();
    if (!clean) { setErr("Full name is required."); return; }
    setSaving(true);
    setErr(null);
    const { error } = await supabase.from("profiles")
      .update({ full_name: clean, phone: phone.trim() || null })
      .eq("id", user.id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    showToast("Profile updated");
    reload();
  };

  const changePassword = async () => {
    setPwSending(true);
    setErr(null);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (!error) await supabase.rpc("log_account_event", { p_event: "password_change_requested" });
    setPwSending(false);
    if (error) { setErr(error.message); return; }
    setPwSent(true);
  };

  return (
    <Box data-testid="account-settings-page" maxW="680px">
      <Heading fontFamily="heading" fontWeight="600" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>
        Account Settings
      </Heading>
      <Text color={colors.textSecondary} fontSize={{ base: "sm", md: "md" }} mb="7">
        Your personal details and security preferences.
      </Text>

      {err && (
        <Box mb="4" px="4" py="2.5" borderRadius="10px" bg={colors.criticalBg} color={colors.criticalText}
          fontSize="sm" data-testid="account-error">{err}</Box>
      )}

      {/* ---------------- Profile ---------------- */}
      <Box mb="9" data-testid="account-profile-section">
        <SectionHeader>Profile</SectionHeader>

        <Flex align="center" gap="4" py="4" borderBottom="1px solid" borderColor={colors.border}>
          <Flex align="center" justify="center" w="64px" h="64px" borderRadius="full" bg={colors.sage}
            color={colors.white} fontFamily="heading" fontWeight="700" fontSize="xl" data-testid="account-avatar">
            {initials}
          </Flex>
          <Box>
            <Text fontWeight="600" color={colors.textPrimary}>{displayName}</Text>
            <Text fontSize="xs" color={colors.textSecondary}>Your initials are shown across Hakina Safety.</Text>
          </Box>
        </Flex>

        <Row label="Full name" testId="account-row-name">
          <Input value={name} onChange={(e) => setName(e.target.value)} w={{ base: "full", md: "280px" }}
            bg={colors.white} borderColor={colors.border} borderRadius="8px" data-testid="account-name-input" />
        </Row>

        <Row label="Phone number" note="Used for SMS safety alerts." testId="account-row-phone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="+1 555 123 4567"
            w={{ base: "full", md: "280px" }} bg={colors.white} borderColor={colors.border} borderRadius="8px"
            data-testid="account-phone-input" />
        </Row>

        <Row label="Email" note="This is your login. Changing it requires verification — coming later." testId="account-row-email">
          <Text fontSize="sm" color={colors.textSecondary} data-testid="account-email-value">{user?.email}</Text>
        </Row>

        <Row label="Company" testId="account-row-company">
          <Text fontSize="sm" color={colors.textSecondary} data-testid="account-company-value">{scope}</Text>
        </Row>

        <Row label="Role" last testId="account-row-role">
          <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="3" py="1" fontSize="xs"
            fontWeight="600" data-testid="account-role-pill">{roleLabel}</Badge>
        </Row>

        <Flex justify="flex-end" mt="4">
          <Button onClick={saveProfile} loading={saving} data-testid="account-save-btn">Save changes</Button>
        </Flex>
      </Box>

      {/* ---------------- Security ---------------- */}
      <Box mb="8" data-testid="account-security-section">
        <SectionHeader>Security</SectionHeader>

        <Row label="Password" note="We'll email you a secure link to set a new password." last={!showSessionSecurity}
          testId="account-row-password">
          {pwSent ? (
            <Text fontSize="sm" color={colors.resolvedText} data-testid="account-pw-sent">
              Reset link sent to {user?.email}. Check your inbox.
            </Text>
          ) : (
            <Button variant="outline" size="sm" onClick={changePassword} loading={pwSending}
              data-testid="account-change-password-btn">
              <KeyRound size={15} /> Change password
            </Button>
          )}
        </Row>

        {showSessionSecurity && <SessionSecurityRow />}
      </Box>

      {/* ---------------- About this build ---------------- */}
      <AboutBuild />
    </Box>
  );
}

const AboutBuild = () => {
  const [swVer, setSwVer] = useState("…");
  useEffect(() => { fetchSwVersion().then(setSwVer); }, []);
  return (
    <Box mb="8" data-testid="account-about-section">
      <SectionHeader>About this build</SectionHeader>
      <Row label="App version" testId="account-row-version">
        <Text fontSize="sm" color={colors.textSecondary} data-testid="account-app-version">v{APP_VERSION}</Text>
      </Row>
      <Row label="Build" note="Quote these when reporting a problem — they prove exactly which build you're running."
        testId="account-row-build">
        <Text fontSize="sm" color={colors.textSecondary} data-testid="account-build-id">
          {bundleHash()}{BUILD_ID !== "local" ? ` · ${BUILD_ID}` : ""}{BUILD_DATE ? ` · ${BUILD_DATE}` : ""}
        </Text>
      </Row>
      <Row label="Offline cache" last testId="account-row-sw">
        <Text fontSize="sm" color={colors.textSecondary} data-testid="account-sw-version">{swVer}</Text>
      </Row>
    </Box>
  );
};
